import { Audited, ID } from '@ianesthesie-kit/services';
import { MedicalComponent } from '@/modules/surgicalEvent';
import { ChipProps } from '@mui/material';

export enum MedicationInterruptionStatus {
  KEEP = 'KEEP',
  STOP_X_DAYS_BEFORE = 'STOP_X_DAYS_BEFORE',
  STOP_ON_THE_DAY_OF_SURGERY = 'STOP_ON_THE_DAY_OF_SURGERY',
  STOP_ON_THE_NIGHT_BEFORE_SURGERY = 'STOP_ON_THE_NIGHT_BEFORE_SURGERY',
  STOP_AND_BRIDGE = 'STOP_AND_BRIDGE',
  BRIDGING = 'BRIDGING',
  TO_REVIEW = 'TO_REVIEW',
  OTHER = 'OTHER',
  UNKNOWN = 'UNKNOWN',
  NOT_SUPPORTED = 'NOT_SUPPORTED'
}

export type UpdatableByDoctorMedicationInterruptionStatus =
  | MedicationInterruptionStatus.KEEP
  | MedicationInterruptionStatus.STOP_X_DAYS_BEFORE
  | MedicationInterruptionStatus.STOP_ON_THE_DAY_OF_SURGERY
  | MedicationInterruptionStatus.STOP_ON_THE_NIGHT_BEFORE_SURGERY
  | MedicationInterruptionStatus.STOP_AND_BRIDGE
  | MedicationInterruptionStatus.OTHER;

export const isStable = (
  st?: MedicationInterruptionStatus
): st is UpdatableByDoctorMedicationInterruptionStatus =>
  st === MedicationInterruptionStatus.KEEP ||
  st === MedicationInterruptionStatus.STOP_X_DAYS_BEFORE ||
  st === MedicationInterruptionStatus.STOP_ON_THE_DAY_OF_SURGERY ||
  st === MedicationInterruptionStatus.STOP_ON_THE_NIGHT_BEFORE_SURGERY ||
  st === MedicationInterruptionStatus.STOP_AND_BRIDGE ||
  st === MedicationInterruptionStatus.OTHER;

export const isUpdatableByDoctor = isStable;

export const interruptionStatusColors: {
  [key in MedicationInterruptionStatus]: ChipProps['color'];
} = {
  [MedicationInterruptionStatus.KEEP]: 'riskNone',
  [MedicationInterruptionStatus.STOP_X_DAYS_BEFORE]: 'riskHigh',
  [MedicationInterruptionStatus.STOP_ON_THE_DAY_OF_SURGERY]: 'warning',
  [MedicationInterruptionStatus.STOP_ON_THE_NIGHT_BEFORE_SURGERY]: 'warning',
  [MedicationInterruptionStatus.STOP_AND_BRIDGE]: 'riskHigh',
  [MedicationInterruptionStatus.BRIDGING]: 'warning',
  [MedicationInterruptionStatus.TO_REVIEW]: 'warning',
  [MedicationInterruptionStatus.UNKNOWN]: 'riskHigh',
  [MedicationInterruptionStatus.OTHER]: 'riskMed',
  [MedicationInterruptionStatus.NOT_SUPPORTED]: 'riskNone'
};

export interface Medication extends MedicalComponent, Audited {
  name: string;
  dosage?: string;
  atc?: string;
  rxNorm?: string;
  interruptionStatus?: MedicationInterruptionStatus;
  otherInterruptionStatusText?: string;
  stopDaysBefore?: number;
  bridgedBy?: Medication[];
  missingAutomationProperties?: MedicationInterruptionAutomationProperty[];
  automationPayload?: Record<string, any>;
  owningPrescriptionId?: ID;
  nameTranslatable: boolean;
  dosageTranslatable: boolean;
  valuesAutomated?: boolean;
  glp1Periodicity?:
    | 'GLP1_DAILY'
    | 'GLP1_WEEKLY'
    | 'GLP1_EITHER'
    | 'GLP1_UNCERTAIN';
  glp1LastInjectionDate?: string;
}

export interface MedicationInterruptionAutomationProperty {
  type: 'CONTEXTUAL' | 'PAYLOAD';
  name: string;
  possibleValues?: string[];
  atcPrefixGroup: string;
}

export interface MedicationAtc {
  medicationName: string;
  atc?: string;
  rxNorm?: string;
}

export const isAtcClass = (
  atcPrefix: string,
  medication: Partial<Medication> | undefined
): boolean => {
  if (!medication?.atc || !atcPrefix) {
    return false;
  }
  return medication.atc.startsWith(atcPrefix);
};

export const sortAutomationProps = (
  a: MedicationInterruptionAutomationProperty,
  b: MedicationInterruptionAutomationProperty
) => {
  if (!a) {
    return 1;
  }
  if (!b) {
    return -1;
  }

  if (a.type === 'CONTEXTUAL') {
    return -1;
  }
  if (b.type === 'CONTEXTUAL') {
    return 1;
  }

  if (a.name === 'intracranialNeurosurgery' || a.name === 'hemorrhagicRisk') {
    return -1;
  }

  return 0;
};
